import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { 
  Table, 
  Button, 
  Space, 
  Tag, 
  Popconfirm, 
  message, 
  Input, 
  Select, 
  Typography,
  Alert,
  Radio,
  Divider,
  Popover
} from 'antd';
import { 
  DeleteOutlined,
  UserOutlined,
  EyeOutlined,
  DownOutlined,
  RightOutlined,
  InfoCircleOutlined
} from '@ant-design/icons';
import { FiEdit, FiTrash2, FiSave, FiX, FiExternalLink, FiCheck } from 'react-icons/fi';
import { ResetIcon, RefreshIcon } from '../../components/common/ActionIcons';
import { TbChartCohort } from "react-icons/tb";
import { HiMiniUsers } from "react-icons/hi2";
import { BsFillFileSpreadsheetFill } from "react-icons/bs";
import { MdLocationOn } from "react-icons/md";
import { FaArrowsDownToPeople } from "react-icons/fa6";
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import dayjs from '../../utils/dayjs';
import PageWrapper from '../../components/common/PageWrapper';
import { showApiError } from '../../utils/formatApiError';
import { 
  filterBuilder, 
  removeFilter, 
  getFilterValue, 
  clearFilters, 
  hasFilter, 
  getAllFilters,
  toggleFilter,
  DEFAULT_CONFIG 
} from '../../utils/functions';

import { 
  listSubjects, 
  listSubjectsByIds, 
  deleteSubject, 
  updateSubject, 
  loadSubjectDto 
} from '../../actions/subjectActions';
import { listCohorts, listCohortsByIds } from '../../actions/cohortActions';
import { listUploadBatches, listUploadBatchesByIds } from '../../actions/uploadBatchActions';
import { listLocations, listLocationsByIds } from '../../actions/locationActions';
import SingleEntitySelectMiniature from '../../components/crud/SingleEntitySelectMiniature.component';
import SubjectDetails from '../../components/patient/subject/SubjectDetails.component';
import SendBatchButton from '../../components/uploadBatch/SendBatchButton.component';
import ApiErrorAlert from '../../components/common/ApiErrorAlert';

const { Text } = Typography;

const SubjectListScreen = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const tableRef = useRef(null);

  // Redux state
  const subjectList = useSelector(state => state.subjectList);
  const { loading: loadingList, subjects = [], error: errorList, currentPage, totalPages, totalCount, statistics } = subjectList;

  const subjectListByIds = useSelector(state => state.subjectListByIds);
  const { loading: loadingListByIds, subjects: subjectsByIds = [], error: errorListByIds } = subjectListByIds;

  const subjectDelete = useSelector(state => state.subjectDelete);
  const { loading: loadingDelete, success: successDelete, error: errorDelete } = subjectDelete;

  const subjectUpdate = useSelector(state => state.subjectUpdate);
  const { loading: loadingUpdate, success: successUpdate, error: errorUpdate, subject: updatedSubject } = subjectUpdate;

  // Local state
  const [filter, setFilter] = useState({
    query: '',
    filter: 'subjectIdentifier',
    page: 1,
    limit: 10,
    sortBy: 'createdAt',
    sortOrder: 'desc'
  });
  const [editingRowId, setEditingRowId] = useState(null);
  const [editingData, setEditingData] = useState({});
  const [searchText, setSearchText] = useState('');
  const [searchProp, setSearchProp] = useState('subjectIdentifier');
  const [statusFilter, setStatusFilter] = useState('all');
  const [cohortFilter, setCohortFilter] = useState(null);
  const [uploadBatchFilter, setUploadBatchFilter] = useState(null);
  const [locationFilter, setLocationFilter] = useState(null);
  const [nhsNumberFilter, setNhsNumberFilter] = useState('all');
  const [geographicalScopeFilter, setGeographicalScopeFilter] = useState('all');
  const [submissionFilter, setSubmissionFilter] = useState('all');
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [isBulkDeleting, setIsBulkDeleting] = useState(false);
  const [expandedRowKeys, setExpandedRowKeys] = useState([]);
  const [selectedSubjectId, setSelectedSubjectId] = useState(null);
  
  // Local state to track subjects for immediate updates
  const [localSubjects, setLocalSubjects] = useState([]);

  // Reset Redux state on component mount - following DynamicFormSP pattern
  useEffect(() => {
    dispatch({ type: 'SUBJECT_DELETE_RESET' });
    dispatch({ type: 'SUBJECT_UPDATE_RESET' });
    dispatch({ type: 'SUBJECT_DETAILS_RESET' });
    
    // Cleanup function to reset subject details when component unmounts
    return () => {
      dispatch({ type: 'SUBJECT_DETAILS_RESET' });
    };
  }, [dispatch]);

  // Load subjects on component mount - following DynamicFormSP pattern
  useEffect(() => {
    dispatch(listSubjects({ ...filter, includeStatistics: false }));
  }, [dispatch]);

  // Listen to filter changes for 'all' mode - separate useEffect like DynamicFormSP
  useEffect(() => {
    dispatch(listSubjects({ ...filter, includeStatistics: false }));
    // Clear expanded rows when filter changes to prevent showing wrong data
    setExpandedRowKeys([]);
  }, [dispatch, filter]);

  // Sync local filter states with actual filter values
  useEffect(() => {
    // Update local filter states based on current filter values
    const statusValue = getFilterValue(filter, 'status');
    setStatusFilter(statusValue || 'all');

    const nhsValue = getFilterValue(filter, 'isNHSNumberValid');
    setNhsNumberFilter(nhsValue || 'all');

    const geoValue = getFilterValue(filter, 'isInGeographicalScope');
    setGeographicalScopeFilter(geoValue || 'all');

    const submissionValue = getFilterValue(filter, 'isSelectedForSubmission');
    setSubmissionFilter(submissionValue || 'all');

    const cohortValue = getFilterValue(filter, 'cohortId');
    setCohortFilter(cohortValue || null);

    const uploadBatchValue = getFilterValue(filter, 'uploadBatchId');
    setUploadBatchFilter(uploadBatchValue || null);

    const locationValue = getFilterValue(filter, 'locationId');
    setLocationFilter(locationValue || null);

    // Update search text and prop based on first filter
    if (filter.filter && filter.query) {
      const filterArray = filter.filter.split(',').map(f => f.trim());
      const queryArray = filter.query.split(',').map(q => q.trim());
      
      // Find the first searchable filter (not status, boolean filters)
      const searchableFilters = ['subjectIdentifier', 'patientId', 'postcode', 'studyRole'];
      const searchIndex = filterArray.findIndex(f => searchableFilters.includes(f));
      
      if (searchIndex !== -1) {
        setSearchProp(filterArray[searchIndex]);
        setSearchText(queryArray[searchIndex]);
      }
    }
  }, [filter]);

  // Update local subjects when Redux subjects change - following DynamicFormSP pattern
  useEffect(() => {
    if (subjects) {
      setLocalSubjects(subjects);
    }
  }, [subjects]);

  // Handle successful operations - following DynamicFormSP pattern
  useEffect(() => {
    if (successDelete) {
      message.success(t('Subject deleted successfully'));
      handleRefresh();
      dispatch({ type: 'SUBJECT_DELETE_RESET' });
    }
  }, [successDelete, dispatch, t]);

  // Handle update success - following DynamicFormSP pattern
  useEffect(() => {
    if (successUpdate && editingRowId && updatedSubject) {
      setLocalSubjects(prevSubjects => 
        prevSubjects.map(subject => 
          subject.id === updatedSubject.id ? { ...subject, ...updatedSubject } : subject
        )
      );
      message.success(t('Subject updated successfully'));
      setEditingRowId(null);
      setEditingData({});
      dispatch({ type: 'SUBJECT_UPDATE_RESET' });
    }
  }, [successUpdate, editingRowId, t, updatedSubject, dispatch]);

  // Handle enrollment success
  useEffect(() => {
    if (successUpdate && !editingRowId && updatedSubject) {
      setLocalSubjects(prevSubjects => 
        prevSubjects.map(subject => 
          subject.id === updatedSubject.id ? { ...subject, ...updatedSubject } : subject
        )
      );
      message.success(t('Subject enrolled successfully'));
      dispatch({ type: 'SUBJECT_UPDATE_RESET' });
    }
  }, [successUpdate, editingRowId, t, updatedSubject, dispatch]);

  // Handle errors - following DynamicFormSP pattern
  useEffect(() => {
    if (errorUpdate && editingRowId) {
      message.error(Array.isArray(errorUpdate) ? errorUpdate.join(', ') : errorUpdate);
      dispatch({ type: 'SUBJECT_UPDATE_RESET' });
    }
    if (errorDelete) {
      showApiError(errorDelete, t);
      dispatch({ type: 'SUBJECT_DELETE_RESET' });
    }
  }, [errorUpdate, errorDelete, editingRowId, t, dispatch]);

  // Filter local subjects based on status filter and search
  const filteredLocalSubjects = React.useMemo(() => {
    // Don't filter locally - let the backend handle all filtering
    return localSubjects;
  }, [localSubjects]);

  // Handlers
  const handleRefresh = useCallback(() => {
    dispatch(listSubjects({ ...filter, includeStatistics: false }));
  }, [dispatch, filter]);

  const handleSearch = useCallback((value) => {
    if (value) {
      // Add/update search filter in combined filters
      setFilter(prev => filterBuilder(prev, searchProp, value, { combine: true, replace: true }));
    } else {
      // Remove search filter from combined filters
      setFilter(prev => removeFilter(prev, searchProp));
    }
  }, [searchProp]);

  const handleDelete = useCallback((id) => {
    dispatch(deleteSubject(id));
  }, [dispatch]);

  const handleBulkDelete = useCallback(async () => {
    setIsBulkDeleting(true);
    try {
      for (const key of selectedRowKeys) {
        dispatch(deleteSubject(key));
        await new Promise(resolve => setTimeout(resolve, 100));
      }
      message.success(t('Selected subjects deleted successfully'));
    } catch (error) {
      message.error(t('Failed to delete some subjects. Please try again.'));
    } finally {
      setSelectedRowKeys([]);
      setIsBulkDeleting(false);
    }
  }, [selectedRowKeys, dispatch, t]);

  const handleEdit = useCallback((record) => {
    setEditingRowId(record.id);
    setEditingData({ ...record });
  }, []);

  const handleSave = useCallback(() => {
    if (editingRowId && editingData) {
      const currentSubject = localSubjects.find(s => s.id === editingRowId);
      const changes = {};
      Object.keys(editingData).forEach(key => {
        if (editingData[key] !== currentSubject[key]) {
          changes[key] = editingData[key];
        }
      });

      if (Object.keys(changes).length > 0) {
        dispatch(updateSubject(editingRowId, changes));
      } else {
        setEditingRowId(null);
        setEditingData({});
      }
    }
  }, [editingRowId, editingData, localSubjects, dispatch]);

  const handleCancel = useCallback(() => {
    setEditingRowId(null);
    setEditingData({});
  }, []);

  const handleInputChange = useCallback((field, value) => {
    setEditingData(prev => ({ ...prev, [field]: value }));
  }, []);

  const handleTableChange = useCallback((pagination, filters, sorter) => {
    setFilter(prev => ({
      ...prev,
      page: pagination.current,
      limit: pagination.pageSize,
      sortBy: sorter.field || 'createdAt',
      sortOrder: sorter.order === 'ascend' ? 'asc' : 'desc'
    }));
  }, []);

  const handleRowClick = useCallback((record) => {
    setSelectedSubjectId(record.id);
  }, []);

  const handleSubjectIdClick = useCallback((record, e) => {
    e.stopPropagation(); // Prevent row click event
    setSelectedSubjectId(record.id);
    
    // Toggle row expansion
    if (expandedRowKeys.includes(record.id)) {
      setExpandedRowKeys(prev => prev.filter(key => key !== record.id));
    } else {
      setExpandedRowKeys(prev => [...prev, record.id]);
    }
  }, [expandedRowKeys]);

  const handleCohortChange = useCallback((cohortId, cohort, record) => {
    // Store the cohort change in editing data instead of immediate update
    setEditingRowId(record.id);
    setEditingData(prev => ({ ...prev, cohortId }));
  }, []);

  const handlePatientDetails = useCallback((patientId) => {
    window.open(`/patients/${patientId}`, '_blank');
  }, []);

  // Handler for enrolling a subject
  const handleEnroll = useCallback((record) => {
    const enrollmentData = {
      enrollmentStatus: 'ENROLLED',
      enrollmentDate: new Date().toISOString(),
      isSelectedForSubmission: true
    };
    dispatch(updateSubject(record.id, enrollmentData));
  }, [dispatch]);

  // Filter options
  const searchOptions = [
    { value: 'subjectIdentifier', label: t('Subject ID') },
    { value: 'patientId', label: t('Patient ID') },
    { value: 'postcode', label: t('Postcode') },
    { value: 'studyRole', label: t('Study Role') },
    { value: 'enrollmentStatus', label: t('Enrollment Status') },
    { value: 'approvalStatus', label: t('Approval Status') }
  ];

  const statusOptions = [
    { label: t('All'), value: 'all' },
    { label: t('Approved'), value: 'APPROVED' },
    { label: t('Rejected'), value: 'REJECTED' },
    { label: t('Pending'), value: 'PENDING' },
    { label: t('Provisionally Approved'), value: 'PROVISIONALLY_APPROVED' }
  ];

  const nhsNumberOptions = [
    { label: t('All'), value: 'all' },
    { label: t('Valid'), value: 'true' },
    { label: t('Invalid'), value: 'false' }
  ];

  const geographicalScopeOptions = [
    { label: t('All'), value: 'all' },
    { label: t('Yes'), value: 'true' },
    { label: t('No'), value: 'false' }
  ];

  const submissionOptions = [
    { label: t('All'), value: 'all' },
    { label: t('Yes'), value: 'true' },
    { label: t('No'), value: 'false' }
  ];

  const handleSearchPropChange = (value) => {
    setSearchProp(value);
    setFilter(prev => ({ ...prev, filter: value, query: searchText, page: 1 }));
  };

  const handleStatusFilterChange = (e) => {
    const value = e.target.value;
    setStatusFilter(value);
    
    if (value === 'all') {
      setFilter(prev => removeFilter(prev, 'status'));
    } else {
      setFilter(prev => filterBuilder(prev, 'status', value, { combine: true, replace: true }));
    }
  };

  const handleCohortFilterChange = (cohortId, cohort) => {
    setCohortFilter(cohortId);
    
    if (!cohortId) {
      setFilter(prev => removeFilter(prev, 'cohortId'));
    } else {
      setFilter(prev => filterBuilder(prev, 'cohortId', cohortId, { combine: true, replace: true }));
    }
  };

  const handleUploadBatchFilterChange = (uploadBatchId, uploadBatch) => {
    setUploadBatchFilter(uploadBatchId);
    
    if (!uploadBatchId) {
      setFilter(prev => removeFilter(prev, 'uploadBatchId'));
    } else {
      setFilter(prev => filterBuilder(prev, 'uploadBatchId', uploadBatchId, { combine: true, replace: true }));
    }
  };

  const handleLocationFilterChange = (locationId, location) => {
    setLocationFilter(locationId);
    
    if (!locationId) {
      setFilter(prev => removeFilter(prev, 'locationId'));
    } else {
      setFilter(prev => filterBuilder(prev, 'locationId', locationId, { combine: true, replace: true }));
    }
  };

  const handleNhsNumberFilterChange = (value) => {
    setNhsNumberFilter(value);
    
    if (value === 'all') {
      setFilter(prev => removeFilter(prev, 'isNHSNumberValid'));
    } else {
      setFilter(prev => filterBuilder(prev, 'isNHSNumberValid', value, { combine: true, replace: true }));
    }
  };

  const handleGeographicalScopeFilterChange = (value) => {
    setGeographicalScopeFilter(value);
    
    if (value === 'all') {
      setFilter(prev => removeFilter(prev, 'isInGeographicalScope'));
    } else {
      setFilter(prev => filterBuilder(prev, 'isInGeographicalScope', value, { combine: true, replace: true }));
    }
  };

  const handleSubmissionFilterChange = (value) => {
    setSubmissionFilter(value);
    
    if (value === 'all') {
      setFilter(prev => removeFilter(prev, 'isSelectedForSubmission'));
    } else {
      setFilter(prev => filterBuilder(prev, 'isSelectedForSubmission', value, { combine: true, replace: true }));
    }
  };

  const handleReset = () => {
    setSearchText('');
    setSearchProp('subjectIdentifier');
    setStatusFilter('all');
    setCohortFilter(null);
    setUploadBatchFilter(null);
    setLocationFilter(null);
    setNhsNumberFilter('all');
    setGeographicalScopeFilter('all');
    setSubmissionFilter('all');
    setFilter(clearFilters(filter, null, { defaultFilter: 'subjectIdentifier' }));
  };

  // Helper functions
  const getStatusTag = (status) => {
    const statusColors = {
      'APPROVED': 'green',
      'REJECTED': 'red',
      'PENDING': 'orange',
      'PENDING_REVIEW': 'orange',
      'PROVISIONALLY_APPROVED': 'green',
      'ENROLLED': 'green'
    };
    
    return (
      <Tag color={statusColors[status] || 'default'}>
        {status}
      </Tag>
    );
  };

  // Expandable row configuration - following UploadBatchList pattern
  const expandedRowRender = (record) => {
    return <SubjectDetails key={record.id} subjectId={record.id} />;
  };

  const expandable = {
    expandedRowRender,
    rowExpandable: (record) => true,
    expandedRowKeys,
    onExpandedRowsChange: setExpandedRowKeys,
  };

  // Table columns - simplified
  const columns = [
    {
      title: t('Actions'),
      key: 'actions',
      fixed: 'left',
      width: 240,
      render: (_, record) => {
        const isEditing = editingRowId === record.id;
        const isEnrolled = record.enrollmentStatus === 'ENROLLED' || record.enrollmentStatus === 'SENT' || record.enrollmentStatus === 'FINAL';
        
        return (
          <Space size="small">
            {isEditing ? (
              <>
                <Button type="primary" size="small" icon={<FiSave />} onClick={handleSave} loading={loadingUpdate} />
                <Button size="small" icon={<FiX />} onClick={handleCancel} />
              </>
            ) : (
              <>
                <Button type="text" size="small" icon={<FiEdit />} onClick={() => handleEdit(record)} />
                <Popconfirm
                  title={t('Delete Subject')}
                  onConfirm={() => handleDelete(record.id)}
                  okText={t('Yes')}
                  cancelText={t('No')}
                >
                  <Button type="text" size="small" icon={<FiTrash2 />} danger loading={loadingDelete} />
                </Popconfirm>
                {!isEnrolled && (
                  <Popconfirm
                    title={t('Enroll Subject')}
                    description={t('Are you sure you want to enroll this subject?')}
                    onConfirm={() => handleEnroll(record)}
                    okText={t('Yes')}
                    cancelText={t('No')}
                  >
                    <Button 
                      type="text" 
                      size="small"
                      icon={<FiCheck />}
                      title={t('Enroll Subject')}
                    >
                      {t('Approve')}
                    </Button>
                  </Popconfirm>
                )}
              </>
            )}
          </Space>
        );
      }
    },
    {
      title: t('Subject ID'),
      key: 'subjectIdentifier',
      width: 300,
      render: (_, record) => {
        const isEditing = editingRowId === record.id;
        const isSelected = selectedSubjectId === record.id;
        return (
          <div style={{ 
            fontWeight: isSelected ? 'bold' : 'normal',
            padding: '4px',
            borderRadius: '4px'
          }}>
            {isEditing ? (
              <Input
                value={editingData.subjectIdentifier || record.subjectIdentifier}
                onChange={(e) => handleInputChange('subjectIdentifier', e.target.value)}
                size="small"
                placeholder="Subject ID"
              />
            ) : (
              <div 
                onClick={(e) => handleSubjectIdClick(record, e)}
                style={{ 
                  cursor: 'pointer',
                  transition: 'background-color 0.2s'
                }}
              >
                <div style={{ fontSize: '14px', fontWeight: 'bold' }}>
                 {record.subjectIdentifier}
                </div>
              </div>
            )}
          </div>
        );
      }
    },
    {
      title: t('Patient'),
      key: 'patient',
      width: 80,
      render: (_, record) => (
        <Button
          type="link"
          size="small"
          icon={<FiExternalLink />}
          onClick={() => handlePatientDetails(record.patientId)}
        >
          {t('View')}
        </Button>
      )
    },
    {
      title: t('Cohort'),
      key: 'cohort',
      width: 200,
      render: (_, record) => {
        const isEditing = editingRowId === record.id;
        return isEditing ? (
          <SingleEntitySelectMiniature
            singularEntity="cohort"
            pluralEntity="cohorts"
            listEntities={listCohorts}
            listEntitiesByIds={listCohortsByIds}
            value={editingData.cohortId || record.cohortId}
            onChange={(cohortId, cohort) => handleInputChange('cohortId', cohortId)}
            primarySearchProp="name"
            secondarySearchProp="description"
            placeholder={t('Select cohort')}
            navigateUrl='/cohorts'
            navigateLabel='Upload Session Management'
            isMultiSelect={false}
            enableCreate={false}
            enableEditing={false}
            icon={<TbChartCohort />}

          />
        ) : (
          <SingleEntitySelectMiniature
            singularEntity="cohort"
            pluralEntity="cohorts"
            listEntities={listCohorts}
            listEntitiesByIds={listCohortsByIds}
            value={record.cohortId}
            onChange={(cohortId, cohort) => handleCohortChange(cohortId, cohort, record)}
            primarySearchProp="name"
            secondarySearchProp="description"
            placeholder={t('Select cohort')}
            isMultiSelect={false}
            enableCreate={false}
            enableEditing={false}
            icon={<TbChartCohort />}

          />
        );
      }
    },
    {
      title: t('System Status'),
      dataIndex: 'status',
      key: 'status',
      width: 220,
      render: (text, record) => {
        const isEditing = editingRowId === record.id;
        return isEditing ? (
          <Select
            value={editingData.status || text}
            onChange={(value) => handleInputChange('status', value)}
            size="small"
            style={{ width: '100%' }}
          >
            <Select.Option value="PROVISIONALLY_APPROVED">PROVISIONALLY_APPROVED</Select.Option>
            <Select.Option value="REJECTED">REJECTED</Select.Option>
            <Select.Option value="PENDING">PENDING</Select.Option>
            <Select.Option value="REVIEW">REVIEW</Select.Option>
          </Select>
        ) : (
          getStatusTag(text)
        );
      }
    },
    {
      title: t('Enrollment Status'),
      dataIndex: 'enrollmentStatus',
      key: 'enrollmentStatus',
      width: 130,
      render: (text, record) => {
        const isEditing = editingRowId === record.id;
        return isEditing ? (
          <Select
            value={editingData.enrollmentStatus || text}
            onChange={(value) => handleInputChange('enrollmentStatus', value)}
            size="small"
            style={{ width: '100%' }}
          >
            <Select.Option value="ENROLLED">ENROLLED</Select.Option>
            <Select.Option value="PENDING">PENDING</Select.Option>
            <Select.Option value="WITHDRAWN">WITHDRAWN</Select.Option>
            <Select.Option value="COMPLETED">COMPLETED</Select.Option>
          </Select>
        ) : (
          getStatusTag(text)
        );
      }
    },
    {
      title: t('Approval Reason'),
      dataIndex: 'approvalReason',
      key: 'approvalReason',
      width: 200,
      render: (text, record) => {
        const isEditing = editingRowId === record.id;
        return isEditing ? (
          <Input.TextArea
            value={editingData.approvalReason || text}
            onChange={(e) => handleInputChange('approvalReason', e.target.value)}
            size="small"
            rows={2}
            placeholder="Approval reason"
          />
        ) : (
          <Text style={{ fontSize: '12px' }} ellipsis={{ tooltip: text }}>
            {text || '-'}
          </Text>
        );
      }
    },
    {
      title: t('Enrollment Date'),
      dataIndex: 'enrollmentDate',
      key: 'enrollmentDate',
      width: 130,
      render: (date) => (
        <Text style={{ fontSize: '12px' }}>
          {date ? dayjs(date).format('DD/MM/YY HH:mm') : '-'}
        </Text>
      )
    },
    {
      title: t('Created'),
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 130,
      render: (date) => (
        <Text style={{ fontSize: '12px' }}>
          {dayjs(date).format('DD/MM/YY HH:mm')}
        </Text>
      )
    },
    {
      title: (
        <span>
          {t('NHS Number')}
          <Popover 
            content={t('Only validated NHS numbers are listed')}
            trigger="hover"
          >
            <InfoCircleOutlined 
              style={{ 
                marginLeft: '4px', 
                color: '#1890ff', 
                cursor: 'help',
                fontSize: '12px'
              }} 
            />
          </Popover>
        </span>
      ),
      dataIndex: 'isNHSNumberValid',
      key: 'isNHSNumberValid',
      width: 120,
      render: (isValid) => (
        <Tag color={isValid ? 'green' : 'red'} size="small">
          {isValid ? t('Valid') : t('Invalid')}
        </Tag>
      )
    },
    {
      title: t('Geographic Scope'),
      dataIndex: 'isInGeographicalScope',
      key: 'isInGeographicalScope',
      width: 140,
      render: (isInScope) => (
        <Tag color={isInScope ? 'green' : 'red'} size="small">
          {isInScope ? t('Yes') : t('No')}
        </Tag>
      )
    },
    {
      title: t('Selected for Submission'),
      dataIndex: 'isSelectedForSubmission',
      key: 'isSelectedForSubmission',
      width: 160,
      render: (isSelected) => (
        <Tag color={isSelected ? 'green' : 'red'} size="small">
          {isSelected ? t('Yes') : t('No')}
        </Tag>
      )
    }
  ];

  const isLoading = loadingList || loadingListByIds;
  const error = errorList || errorListByIds;

  const headerActions = (
    <Space>
      <SendBatchButton 
        initialUploadBatchId={uploadBatchFilter}
        size="small"
      />
      <Button
        type="text"
        icon={<TbChartCohort size={16} />}
        onClick={() => window.open('/cohorts', '_blank')}
        size="small"
        title={t('Browse all cohorts')}
      >
        {t('Cohorts')}
      </Button>
      <Button
        type="text"
        icon={<HiMiniUsers size={16} />}
        onClick={() => window.open('/patients', '_blank')}
        size="small"
        title={t('Browse all patients')}
      >
        {t('Patients')}
      </Button>
    </Space>
  );

  return (
    <PageWrapper
      title={t('Subject Management')}
      subtitle={
        <Space>
          <Text type="secondary" style={{ fontSize: '14px' }}>
            {t('All Subjects')} ({totalCount || 0} {t('total')})
          </Text>
          {statistics && (
            <Tag color="blue" size="small">
              {t('Total Subjects')}: {totalCount || 0}
            </Tag>
          )}
        </Space>
      }
      icon={<FaArrowsDownToPeople size={24} />}
      headerActions={headerActions}
      showCard={true}
      cardProps={{
        size: 'small'
      }}
    >
      <ApiErrorAlert error={error} closable />

      {/* Search and Filter Controls */}
      <div style={{ marginBottom: 16 }}>
        <Space wrap style={{ marginBottom: 12 }}>
          <span style={{ fontSize: '12px', color: '#666' }}>{t('Filter')}:</span>
          <Select
            placeholder={t("Search by")}
            style={{ width: '140px' }}
            size="small"
            value={searchProp}
            onChange={handleSearchPropChange}
          >
            {searchOptions.map((option) => (
              <Select.Option key={option.value} value={option.value}>
                {option.label}
              </Select.Option>
            ))}
          </Select>
          <Input.Search
            placeholder={`${t("Search by")} ${searchOptions.find(opt => opt.value === searchProp)?.label || ''}`}
            onSearch={(value) => {
              setSearchText(value);
              handleSearch(value);
            }}
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            style={{ minWidth: '200px' }}
            size="small"
            enterButton
          />
          
          {/* Cohort Filter */}
          <SingleEntitySelectMiniature
            singularEntity="cohort"
            pluralEntity="cohorts"
            listEntities={listCohorts}
            listEntitiesByIds={listCohortsByIds}
            value={cohortFilter}
            onChange={handleCohortFilterChange}
            primarySearchProp="name"
            secondarySearchProp="description"
            placeholder={t('Filter by cohort')}
            isMultiSelect={false}
            enableCreate={false}
            enableEditing={false}
            icon={<TbChartCohort />}
          />

          {/* Upload Batch Filter */}
          <SingleEntitySelectMiniature
            singularEntity="uploadBatch"
            pluralEntity="uploadBatches"
            listEntities={listUploadBatches}
            listEntitiesByIds={listUploadBatchesByIds}
            value={uploadBatchFilter}
            onChange={handleUploadBatchFilterChange}
            primarySearchProp="fileName"
            secondarySearchProp="id"
            placeholder={t('Filter by upload')}
            isMultiSelect={false}
            enableCreate={false}
            enableEditing={false}
            icon={<BsFillFileSpreadsheetFill />}
          />

          {/* Location Filter */}
          <SingleEntitySelectMiniature
            singularEntity="location"
            pluralEntity="locations"
            listEntities={listLocations}
            listEntitiesByIds={listLocationsByIds}
            value={locationFilter}
            onChange={handleLocationFilterChange}
            primarySearchProp="name"
            secondarySearchProp="locationCode"
            placeholder={t('Filter by location')}
            isMultiSelect={false}
            enableCreate={false}
            enableEditing={false}
            icon={<MdLocationOn />}
          />
          
          <Button
            type="default"
            icon={<ResetIcon />}
            onClick={handleReset}
            size="small"
          >
            {t("Reset")}
          </Button>
          <Button
            icon={<RefreshIcon />}
            onClick={handleRefresh}
            size="small"
          >
            {t('Refresh')}
          </Button>
        </Space>

        {/* All Filters on Same Line */}
        <div>
          <Space size="middle" wrap>
            <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '8px' }}>
              <Text style={{ fontSize: '12px', color: '#666', whiteSpace: 'nowrap' }}>{t('Status')}:</Text>
              <Radio.Group
                value={statusFilter}
                onChange={handleStatusFilterChange}
                size="small"
                buttonStyle="solid"
              >
                {statusOptions.map((option) => (
                  <Radio.Button key={option.value} value={option.value}>
                    {option.label}
                  </Radio.Button>
                ))}
              </Radio.Group>
            </div>
            
            <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '8px' }}>
              <Text style={{ fontSize: '12px', color: '#666', whiteSpace: 'nowrap' }}>
                {t('NHS Number')}:
                <Popover 
                  content={t('Only validated NHS numbers are listed')}
                  trigger="hover"
                >
                  <InfoCircleOutlined 
                    style={{ 
                      marginLeft: '4px', 
                      color: '#1890ff', 
                      cursor: 'help',
                      fontSize: '12px'
                    }} 
                  />
                </Popover>
              </Text>
              <Radio.Group
                value={nhsNumberFilter}
                onChange={(e) => handleNhsNumberFilterChange(e.target.value)}
                size="small"
                buttonStyle="solid"
              >
                {nhsNumberOptions.map((option) => (
                  <Radio.Button key={option.value} value={option.value}>
                    {option.label}
                  </Radio.Button>
                ))}
              </Radio.Group>
            </div>
            
            <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '8px' }}>
              <Text style={{ fontSize: '12px', color: '#666', whiteSpace: 'nowrap' }}>{t('Geographic Scope')}:</Text>
              <Radio.Group
                value={geographicalScopeFilter}
                onChange={(e) => handleGeographicalScopeFilterChange(e.target.value)}
                size="small"
                buttonStyle="solid"
              >
                {geographicalScopeOptions.map((option) => (
                  <Radio.Button key={option.value} value={option.value}>
                    {option.label}
                  </Radio.Button>
                ))}
              </Radio.Group>
            </div>
            
            <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '8px' }}>
              <Text style={{ fontSize: '12px', color: '#666', whiteSpace: 'nowrap' }}>{t('Selected for Submission')}:</Text>
              <Radio.Group
                value={submissionFilter}
                onChange={(e) => handleSubmissionFilterChange(e.target.value)}
                size="small"
                buttonStyle="solid"
              >
                {submissionOptions.map((option) => (
                  <Radio.Button key={option.value} value={option.value}>
                    {option.label}
                  </Radio.Button>
                ))}
              </Radio.Group>
            </div>
          </Space>
        </div>

        {selectedRowKeys.length > 0 && (
          <div style={{ marginTop: 12 }}>
            <Popconfirm
              title={t("Delete selected items")}
              description={t("Are you sure you want to delete these subjects?")}
              okText={t("Yes")}
              cancelText={t("No")}
              onConfirm={handleBulkDelete}
            >
              <Button type="primary" danger loading={isBulkDeleting} size="small">
                {t("Delete")} ({selectedRowKeys.length})
              </Button>
            </Popconfirm>
          </div>
        )}
      </div>

      <div ref={tableRef} style={{ width: '100%', overflow: 'auto' }}>
        <Table
          columns={columns}
          dataSource={filteredLocalSubjects}
          rowKey="id"
          loading={isLoading}
          size="small"
          rowSelection={{
            type: 'checkbox',
            selectedRowKeys,
            onChange: (selectedKeys) => setSelectedRowKeys(selectedKeys),
          }}
          onRow={(record) => ({
            onClick: () => handleRowClick(record),
            style: { 
              cursor: 'pointer',
            }
          })}
          expandable={expandable}
          pagination={{
            current: filter.page,
            pageSize: filter.limit,
            total: totalCount,
            showSizeChanger: true,
            showQuickJumper: (totalCount || 0) > 50,
            showTotal: (total, range) => `${range[0]}-${range[1]} ${t('of')} ${total} ${t('items')}`,
            pageSizeOptions: ['10', '20', '50'],
            size: 'small',
            position: ['bottomCenter']
          }}
          onChange={handleTableChange}
          scroll={{ x: 1400, y: 'calc(100vh - 300px)' }}
          tableLayout="auto"
        />
      </div>
    </PageWrapper>
  );
};

export default SubjectListScreen;
